package com.shadowings.gero.presentation.screen

import com.shadowings.gero.presentation.util.DevicePreviews
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.data.repository.UniliteralRepository
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.LocalIsDarkMode
import com.shadowings.gero.presentation.ui.hieroglyphTint
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.chiudi_btn
import gero.composeapp.generated.resources.favourite_off
import gero.composeapp.generated.resources.favourite_on
import gero.composeapp.generated.resources.hiero_detail_title
import gero.composeapp.generated.resources.hiero_not_seen_hint
import gero.composeapp.generated.resources.label_determinativo
import gero.composeapp.generated.resources.label_ideogramma
import gero.composeapp.generated.resources.label_transliteration
import gero.composeapp.generated.resources.toggle_favorite_desc
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HieroDetailDialog(
    hiero: Hiero,
    seen: Boolean,
    isFavorite: Boolean,
    onToggleFavorite: () -> Unit,
    onDismiss: () -> Unit,
    languageCode: String
) {
    BasicAlertDialog(
        onDismissRequest = onDismiss
    ) {
        HieroDetailDialogContent(
            hiero = hiero,
            seen = seen,
            isFavorite = isFavorite,
            onToggleFavorite = onToggleFavorite,
            onDismiss = onDismiss,
            languageCode = languageCode
        )
    }
}

@Composable
fun HieroDetailDialogContent(
    hiero: Hiero,
    seen: Boolean,
    isFavorite: Boolean,
    onToggleFavorite: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    languageCode: String
) {
    val hapticFeedback = LocalHapticFeedback.current
    val deviceInfo = rememberDeviceInfo()
    val glyphBoxSize = if (deviceInfo.deviceClass == DeviceClass.WEB_FULL) 150.dp else 96.dp

    Surface(
        modifier = modifier.fillMaxWidth().padding(16.dp),
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.surface,
        tonalElevation = 6.dp
    ) {
        Column(
            modifier = Modifier.padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Titolo del geroglifico
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = stringResource(Res.string.hiero_detail_title, hiero.gardinerCode),
                    style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.align(Alignment.Center)
                )
                if (seen) {
                    IconButton(
                        onClick = {
                            hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                            onToggleFavorite()
                        },
                        modifier = Modifier
                            .align(Alignment.CenterEnd)
                            .semantics { testTag = "hiero_detail_btn_toggle_favorite" }
                    ) {
                        Icon(
                            painter = painterResource(if (isFavorite) Res.drawable.favourite_on else Res.drawable.favourite_off),
                            contentDescription = stringResource(Res.string.toggle_favorite_desc),
                            tint = if (isFavorite && LocalIsDarkMode.current) Color.White else Color.Unspecified,
                            modifier = Modifier.size(24.dp)
                        )
                    }
                }
            }

            // Immagine/Segno ingrandito
            Box(
                modifier = Modifier
                    .size(glyphBoxSize)
                    .background(
                        color = if (seen) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant,
                        shape = RoundedCornerShape(16.dp)
                    )
                    .padding(16.dp),
                contentAlignment = Alignment.Center
            ) {
                val res = UniliteralRepository.resFromGardinerCode(hiero.gardinerCode)
                if (res != null) {
                    Image(
                        painter = painterResource(res),
                        contentDescription = hiero.gardinerCode,
                        modifier = Modifier.fillMaxSize(),
                        colorFilter = ColorFilter.tint(
                            hieroglyphTint.copy(alpha = if (seen) 1f else 0.25f)
                        )
                    )
                } else {
                    Text(
                        text = hiero.unicode.ifBlank { "?" },
                        fontSize = 48.sp,
                        color = if (seen) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurface.copy(
                            alpha = 0.3f
                        )
                    )
                }
            }

            // Dettagli in forma tabellare
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                if (seen) {
                    val ideogramma = hiero.getIdeogramma(languageCode)
                    val determinativo = hiero.getDeterminativo(languageCode)
                    val mnemonico = hiero.getMnemonico(languageCode)

                    if (hiero.transliteration != "-") {
                        DetailRow(
                            label = stringResource(Res.string.label_transliteration),
                            value = hiero.transliteration.ifBlank { "-" })
                    }
                    if (ideogramma != "-") {
                        DetailRow(
                            label = stringResource(Res.string.label_ideogramma),
                            value = ideogramma
                        )
                    }
                    if (determinativo != "-") {
                        DetailRow(
                            label = stringResource(Res.string.label_determinativo),
                            value = determinativo
                        )
                    }
                    if (mnemonico != "-") {
                        HorizontalDivider(Modifier.padding(vertical = 4.dp))
                        Text(
                            text = mnemonico,
                            style = MaterialTheme.typography.bodyMedium,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }

                } else {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = stringResource(Res.string.hiero_not_seen_hint),
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
                        style = MaterialTheme.typography.bodyMedium,
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = TextAlign.Center
                    )
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            // Pulsante di conferma
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                Button(
                    onClick = {
                        hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                        onDismiss()
                    },
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    ),
                    modifier = Modifier.semantics { testTag = "hiero_detail_btn_close" }
                ) {
                    Text(stringResource(Res.string.chiudi_btn))
                }
            }
        }
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun HieroDetailDialogPreview() {
    AppTheme {
        Surface {
            HieroDetailDialogContent(
                hiero = Hiero(
                    gardinerCode = "A1",
                    unicode = "𓀀",
                    transliteration = "i",
                    ideogramma = "uomo",
                    determinativo = "uomo, me, mi",
                    mnemonico = "lorem ipsum dolor sit amet consectetur adipiscing elit",
                    ideogrammaEn = "man",
                    determinativoEn = "man, me, I",
                    mnemonicoEn = "lorem ipsum dolor sit amet consectetur adipiscing elit"
                ),
                seen = true,
                isFavorite = false,
                onToggleFavorite = {},
                languageCode = "it",
                onDismiss = {}
            )
        }
    }
}
